import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import { useNavigateContext } from '../../Navigation/NavigateContext'
import { FilterParam } from './FilterBar.types'

export const useClearFilters = (): {
  clearFilters: () => void
  hasAppliedFilters: boolean
} => {
  const location = useLocation()
  const { navigateState } = useNavigateContext()
  const fallBackNavigate = useNavigate()
  const navigate = navigateState.navigate || fallBackNavigate

  const hasAppliedFilters = useMemo(() => {
    /**
     * Current url whose query strings are filtered to include only FilterParams
     */
    const strippedUrl = queryString.pick(
      `${location.pathname}${location.search}`,
      Object.values(FilterParam),
      {
        arrayFormat: 'bracket',
      },
    )

    /** Object of applied FilterParams */
    const filterParamsFallback = queryString.parseUrl(strippedUrl, {
      arrayFormat: 'bracket',
    }).query
    const filterParams = navigateState.parsedQueryParams || filterParamsFallback

    // don't count page as a filter
    delete filterParams[FilterParam.PAGE]

    /** Have any FilterParams have been applied */
    return Object.keys(filterParams).length > 0
  }, [navigateState.parsedQueryParams, location.search, location.pathname])

  const clearFilters = useCallback(() => {
    /** Object of all search Params */
    const allSearchParams = queryString.parse(location.search, {
      arrayFormat: 'bracket',
    })

    // Construct the url with the page number reset
    const url = queryString.stringifyUrl(
      {
        url: location.pathname,
        query: allSearchParams,
      },
      { arrayFormat: 'bracket' },
    )
    // Get all FilterParams except PAGE
    const excludeParams = Object.values(FilterParam).filter(
      (param) => param != FilterParam.PAGE,
    )

    navigate(
      queryString.exclude(url, excludeParams, { arrayFormat: 'bracket' }),
    )
  }, [navigate, location.pathname, location.search])

  return {
    clearFilters,
    hasAppliedFilters,
  }
}
